import React, { useState, useCallback, useEffect } from 'react';
import { initializeBarcodeListener, BarcodeListenerCallbacks } from '../../services/barcodeService';
import { useResponsive } from '../../hooks';
import { InventoryItem } from '../../types';
import { AddItemForm, ItemDetailModal } from '../inventory';
import { isIOS, saveScannerState, getSavedScannerState } from '../../utils/platformUtils';
import { Notification } from '../ui';
import { NotificationType } from '../../types/ui';
import { Button } from '../ui';
import { ButtonVariant, ButtonSize } from '../../types/ui';
import { 
  setGlobalBarcodeListeningStatus,
} from '../../services/barcodeService';

const DesktopScannerButton: React.FC = () => {
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  const isIOSDevice = isIOS();
  
  // Initialize from localStorage if available
  const [isScannerActive, setIsScannerActive] = useState(() => {
    // Only initialize as active if we're not on mobile or iOS
    if (!isMobile && !isIOSDevice) {
      return getSavedScannerState();
    }
    return false;
  });
  
  const [scannedItem, setScannedItem] = useState<InventoryItem | null>(null);
  const [newItemBarcode, setNewItemBarcode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAddItemForm, setShowAddItemForm] = useState(false);
  const [notification, setNotification] = useState<{
    type: NotificationType;
    message: string;
  } | null>(null);

  // Handle barcode scanner callbacks
  const handleItemFound = useCallback((item: InventoryItem) => {
    setScannedItem(item);
  }, []);
  
  const handleNewItemBarcode = useCallback((barcode: string) => {
    setNewItemBarcode(barcode);
    setShowAddItemForm(true);
  }, []);
  
  const handleError = useCallback((error: Error) => {
    console.error('Barcode scanner error:', error);
    
    // Check if this is an EAN-8 validation error
    if (error.message.includes('Geçersiz barkod formatı')) {
      // Show notification for invalid barcode format but keep scanner active
      setNotification({
        type: NotificationType.ERROR,
        message: error.message
      });
      
      // Clear notification after 3 seconds
      setTimeout(() => {
        clearNotification();
      }, 3000);
    } else {
      // For other errors, disable the scanner
      setError(error.message);
      setIsScannerActive(false);
      saveScannerState(false); // Update localStorage on error
    }
  }, []);
  
  // Initialize and clean up barcode listener
  useEffect(() => {
    if (isScannerActive && !isMobile) {
      // Check iOS before attempting to start
      if (isIOSDevice) {
        handleError(new Error('Barkod tarayıcısı iOS cihazlarda desteklenmemektedir.'));
        return;
      }
      
      const callbacks: BarcodeListenerCallbacks = {
        onItemFound: handleItemFound,
        onNewItemBarcode: handleNewItemBarcode,
        onError: handleError
      };
      
      try {
        // Start listening
        const cleanup = initializeBarcodeListener(callbacks);
        
        // Return cleanup function
        return cleanup;
      } catch (error) {
        if (error instanceof Error) {
          handleError(error);
        }
      }
    }
  }, [isScannerActive, isMobile, handleItemFound, handleNewItemBarcode, handleError, isIOSDevice]);
  
  // Persist scanner state to localStorage whenever it changes
  useEffect(() => {
    saveScannerState(isScannerActive);
  }, [isScannerActive]);
  
  // Reset states
  const handleClose = () => {
    setScannedItem(null);
    setNewItemBarcode(null);
    setError(null);
    setShowAddItemForm(false);
  };
  
  const handleSuccess = () => {
    handleClose();
  };
  
  // Toggle scanner active state
  const toggleScanner = () => {
    const newStatus = !isScannerActive;
    setIsScannerActive(newStatus);
    setGlobalBarcodeListeningStatus(newStatus); // Inform service about active status
  };
  
  // Listen for open-scanner event from dashboard
  useEffect(() => {
    const handleOpenScannerEvent = () => {
      setIsScannerActive(true);
      saveScannerState(true);
    };
    document.addEventListener('open-scanner', handleOpenScannerEvent);
    
    return () => {
      document.removeEventListener('open-scanner', handleOpenScannerEvent);
    };
  }, []);
  
  // Function to show notifications
  const showNotification = (type: NotificationType, message: string) => {
    setNotification({ type, message });
  };

  // Function to clear notifications
  const clearNotification = useCallback(() => {
    setNotification(null);
  }, []);
  
  // This effect ensures we clean up the global listener when component unmounts
  useEffect(() => {
    return () => {
      if (isScannerActive) {
        setGlobalBarcodeListeningStatus(false);
      }
    };
  }, [isScannerActive]);
  
  // Skip on mobile, this is for desktop only
  if (isMobile) {
    return null;
  }
  
  return (
    <>
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          onClick={toggleScanner}
          variant={isScannerActive ? ButtonVariant.DANGER : ButtonVariant.PRIMARY}
          size={ButtonSize.LARGE}
          className="shadow-lg rounded-full p-4"
          title={isScannerActive ? "Barkod Okuyucuyu Durdur" : "Barkod Okuyucuyu Başlat"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5zM13.5 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5z" />
          </svg>
        </Button>
      </div>
      
      {/* Error message - now with enhanced styling */}
      {error && (
        <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-l-4 border-red-500 text-red-800 px-6 py-5 rounded-lg shadow-xl max-w-md w-full animate-fadeIn">
          <div className="flex items-center">
            <div className="mr-3 flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="text-base font-semibold mb-1">Hata</p>
              <p className="text-sm text-gray-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Item Details View - shown when item is found from barcode scan */}
      {scannedItem && (
        <ItemDetailModal
          item={scannedItem}
          isOpen={true}
          onClose={handleClose}
          isBarcodeScan={true}
          showNotification={showNotification}
        />
      )}
      
      {/* Add Item Form */}
      {newItemBarcode && (
        <AddItemForm
          isOpen={showAddItemForm}
          initialBarcode={newItemBarcode}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
      
      {/* Display notification with enhanced styling */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={clearNotification}
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 max-w-md w-full shadow-xl backdrop-blur-sm"
          duration={4000}
        />
      )}
    </>
  );
};

export default DesktopScannerButton; 